const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('teacherToken');
  }

  // Set auth token
  setAuthToken(token) {
    this.token = token;
    localStorage.setItem('teacherToken', token);
  }

  // Clear auth token
  clearAuthToken() {
    this.token = null;
    localStorage.removeItem('teacherToken');
  }

  // Get auth headers
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.token) {
      headers['Authorization'] = `Token ${this.token}`;
    }
    
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Don't set default headers for FormData requests
    let headers = {};
    if (!(options.body instanceof FormData)) {
      headers = this.getAuthHeaders();
    } else {
      // For FormData, only set Authorization header
      headers = {
        'Authorization': this.token ? `Token ${this.token}` : '',
      };
    }
    
    const config = {
      headers,
      ...options,
    };

    // Debug logging for live sessions
    if (endpoint.includes('live-sessions')) {
      console.log('API Request Debug:');
      console.log('URL:', url);
      console.log('Headers:', headers);
      console.log('Body:', options.body);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let message = 'Request failed';
        if (errorData) {
          if (typeof errorData.detail === 'string') message = errorData.detail;
          else {
            // Try to pull the first field error
            const firstKey = Object.keys(errorData)[0];
            const firstVal = firstKey ? errorData[firstKey] : null;
            if (Array.isArray(firstVal) && firstVal.length > 0) message = `${firstKey}: ${firstVal[0]}`;
            else message = JSON.stringify(errorData);
          }
        }
        throw new Error(`${message} (status: ${response.status})`);
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      // If no content or not JSON, return empty object
      if (contentLength === '0' || response.status === 204 || !contentType?.includes('application/json')) {
        return {};
      }

      // Try to parse JSON, return empty object if it fails
      try {
        return await response.json();
      } catch (jsonError) {
        console.warn('Failed to parse JSON response:', jsonError);
        return {};
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Teacher Authentication
  async login(credentials) {
    const response = await this.request('/auth/token/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    if (response.auth_token) {
      this.setAuthToken(response.auth_token);
    }
    
    return response;
  }

  async logout() {
    try {
      await this.request('/auth/token/logout/', {
        method: 'POST',
      });
    } finally {
      this.clearAuthToken();
    }
  }

  // Teacher Profile
  async getProfile() {
    return this.request('/accounts/profile/');
  }

  async updateProfile(profileData) {
    return this.request('/accounts/profile/', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Change password (using custom endpoint)
  async changePassword({ current_password, new_password, re_new_password }) {
    return this.request('/accounts/password/change/', {
      method: 'POST',
      body: JSON.stringify({ 
        old_password: current_password,  // Our endpoint uses 'old_password' not 'current_password'
        new_password: new_password,
        re_new_password: re_new_password
      }),
    });
  }

  // Request password reset
  async requestPasswordReset(email) {
    return this.request('/accounts/password/reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Course Management - Get courses assigned to this teacher
  async getMyCourses() {
    try {
      console.log('Fetching teacher courses...');
      const response = await this.request('/courses/courses/');
      console.log('Courses API response:', response);
      
      // Ensure we always return an array (handle paginated and non-paginated)
      let courses = [];
      if (Array.isArray(response)) {
        courses = response;
      } else if (response && Array.isArray(response.results)) {
        courses = response.results;
      } else if (response && Array.isArray(response.data)) {
        courses = response.data;
      }
      console.log('Processed courses:', courses);
      return courses;
    } catch (error) {
      console.error('Failed to get teacher courses:', error);
      console.error('Error details:', error.message);
      return [];
    }
  }

  // Verify teacher authentication
  async verifyTeacherAuth() {
    try {
      const profile = await this.getProfile();
      console.log('Teacher profile:', profile);
      return profile;
    } catch (error) {
      console.error('Teacher authentication failed:', error);
      return null;
    }
  }

  async getCourse(courseId) {
    return this.request(`/courses/courses/${courseId}/`);
  }

  async getCourseModules(courseId) {
    try {
      // Preferred endpoint (allows teacher to also POST)
      const primary = await this.request(`/courses/courses/${courseId}/modules/`);
      if (Array.isArray(primary)) return primary;
      if (primary && Array.isArray(primary.results)) return primary.results;
    } catch (error) {
      console.warn('Primary course modules endpoint failed, attempting fallback:', error?.message);
    }
    // Fallback to module list filtered by course
    try {
      const fallback = await this.request(`/courses/modules/?course=${courseId}`);
      if (Array.isArray(fallback)) return fallback;
      if (fallback && Array.isArray(fallback.results)) return fallback.results;
      return [];
    } catch (err) {
      console.error('Fallback modules endpoint also failed:', err);
      return [];
    }
  }

  async createDefaultModule(courseId) {
    try {
      const moduleData = {
        name: 'General Materials',
        description: 'Default module for course materials',
        order: 1,
        time_slot: 'Flexible',
        is_active: true
      };
      
      return this.request(`/courses/courses/${courseId}/modules/`, {
        method: 'POST',
        body: JSON.stringify(moduleData),
      });
    } catch (error) {
      console.error('Failed to create default module:', error);
      return null;
    }
  }

  async createCourse(courseData) {
    try {
      // Get teacher profile to add teacher ID to course
      const profile = await this.getProfile();
      
      // Handle file uploads
      const formData = new FormData();
      Object.keys(courseData).forEach(key => {
        if (courseData[key] !== null && courseData[key] !== undefined) {
          formData.append(key, courseData[key]);
        }
      });
      
      // Add teacher ID to the course
      if (profile && profile.id) {
        formData.append('teacher', profile.id);
      }

      return this.request('/courses/courses/', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${this.token}`,
          // Don't set Content-Type for FormData, let browser handle it
        },
        body: formData,
      });
    } catch (error) {
      console.error('Failed to create course:', error);
      throw error;
    }
  }

  async updateCourse(courseId, courseData) {
    return this.request(`/courses/courses/${courseId}/`, {
      method: 'PUT',
      body: JSON.stringify(courseData),
    });
  }

  async deleteCourse(courseId) {
    return this.request(`/courses/courses/${courseId}/`, {
      method: 'DELETE',
    });
  }

  // Material Management - Get materials created by this teacher
  async getCourseMaterials(courseId) {
    try {
      // The backend automatically filters by the logged-in teacher
      let response;
      if (!courseId || courseId === 'undefined') {
        response = await this.request('/materials/materials/');
      } else {
        response = await this.request(`/materials/materials/?course=${courseId}`);
      }
      // Normalize: return an array regardless of pagination
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.results)) return response.results;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    } catch (error) {
      console.error('Failed to get teacher materials:', error);
      return [];
    }
  }

  // Materials by module (for ordering and filtering)
  async getMaterialsByModule(moduleId) {
    try {
      const response = await this.request(`/materials/materials/?module=${moduleId}`);
      return Array.isArray(response) ? response : (response?.results || []);
    } catch (error) {
      console.error('Failed to get materials by module:', error);
      return [];
    }
  }

  async getCourseMaterialsBySlug(slug) {
    try {
      // First get the batch to get the course ID, then get materials
      const batch = await this.getBatchBySlug(slug);
      if (batch && batch.course) {
        return this.request(`/materials/materials/?course=${batch.course}`);
      }
      return [];
    } catch (error) {
      console.error('Failed to get teacher materials by slug:', error);
      return [];
    }
  }

  async createMaterial(materialData) {
    try {
      // Get teacher profile to add teacher ID to material
      const profile = await this.getProfile();
      
      // Add teacher ID to the material if it's FormData
      if (materialData instanceof FormData) {
        if (profile && profile.id) {
          materialData.append('teacher', profile.id);
        }
      } else {
        // Handle regular object data
        const formData = new FormData();
        Object.keys(materialData).forEach(key => {
          if (materialData[key] !== null && materialData[key] !== undefined) {
            formData.append(key, materialData[key]);
          }
        });
        
        // Add teacher ID to the material
        if (profile && profile.id) {
          formData.append('teacher', profile.id);
        }
        materialData = formData;
      }

      return this.request('/materials/materials/', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${this.token}`,
          // Don't set Content-Type for FormData
        },
        body: materialData,
      });
    } catch (error) {
      console.error('Failed to create material:', error);
      throw error;
    }
  }

  async updateMaterial(materialId, materialData) {
    try {
      const profile = await this.getProfile();

      // If caller passed a plain object, convert to FormData
      if (!(materialData instanceof FormData)) {
        const formData = new FormData();
        Object.keys(materialData || {}).forEach(key => {
          const value = materialData[key];
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
        materialData = formData;
      }

      // Ensure teacher id is included
      if (profile && profile.id) {
        materialData.append('teacher', profile.id);
      }

      return this.request(`/materials/materials/${materialId}/`, {
        method: 'PUT',
        headers: {
          'Authorization': this.token ? `Token ${this.token}` : '',
        },
        body: materialData,
      });
    } catch (error) {
      console.error('Failed to update material:', error);
      throw error;
    }
  }

  async deleteMaterial(materialId) {
    try {
      // Get teacher profile to ensure they can only delete their own materials
      const profile = await this.getProfile();
      if (!profile || !profile.id) {
        throw new Error('Teacher profile not found');
      }
      
      return this.request(`/materials/materials/${materialId}/`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete material:', error);
      throw error;
    }
  }

  // Batch Management - Get batches assigned to this teacher
  async getMyBatches() {
    try {
      // The backend now automatically filters by the logged-in teacher
      const response = await this.request('/batches/batches/');
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.results)) return response.results;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    } catch (error) {
      console.error('Failed to get teacher batches:', error);
      return [];
    }
  }

  async getBatch(batchId) {
    return this.request(`/batches/batches/${batchId}/`);
  }

  async getBatchBySlug(slug) {
    return this.request(`/batches/batches/${slug}/`);
  }

  async getBatchStudents(batchId) {
    return this.request(`/batches/batches/${batchId}/students/`);
  }

  async getBatchStudentsBySlug(slug) {
    return this.request(`/batches/batches/${slug}/students/`);
  }

  // Student Progress - Use existing progress endpoints
  async getStudentProgress(studentId, courseId) {
    return this.request(`/progress/progress/?student=${studentId}&course=${courseId}`);
  }

  async updateStudentProgress(studentId, courseId, progressData) {
    return this.request(`/progress/progress/${studentId}/`, {
      method: 'PUT',
      body: JSON.stringify(progressData),
    });
  }

  // Live Sessions - Get live sessions for the teacher
  async getLiveSessions() {
    try {
      const response = await this.request('/courses/live-sessions/');
      console.log('Live sessions API response:', response);
      return response;
    } catch (error) {
      console.error('Failed to get live sessions:', error);
      return [];
    }
  }

  async createLiveSession(sessionData) {
    try {
      console.log('Creating live session with data:', sessionData);
      console.log('Current token:', this.token);
      const result = await this.request('/courses/live-sessions/', {
        method: 'POST',
        body: JSON.stringify(sessionData),
      });
      console.log('Live session created successfully:', result);
      return result;
    } catch (error) {
      console.error('Failed to create live session:', error);
      console.error('Error details:', error.message);
      throw error;
    }
  }

  async updateLiveSession(sessionId, sessionData) {
    try {
      return this.request(`/courses/live-sessions/${sessionId}/`, {
        method: 'PUT',
        body: JSON.stringify(sessionData),
      });
    } catch (error) {
      console.error('Failed to update live session:', error);
      throw error;
    }
  }

  async deleteLiveSession(sessionId) {
    try {
      return this.request(`/courses/live-sessions/${sessionId}/`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete live session:', error);
      throw error;
    }
  }

  async startLiveSession(sessionId) {
    try {
      return this.request(`/courses/live-sessions/${sessionId}/start_session/`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Failed to start live session:', error);
      throw error;
    }
  }

  async endLiveSession(sessionId) {
    try {
      return this.request(`/courses/live-sessions/${sessionId}/end_session/`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Failed to end live session:', error);
      throw error;
    }
  }

  async cancelLiveSession(sessionId) {
    try {
      return this.request(`/courses/live-sessions/${sessionId}/cancel_session/`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Failed to cancel live session:', error);
      throw error;
    }
  }

  async createYouTubeStream(sessionId) {
    try {
      return this.request(`/courses/live-sessions/${sessionId}/create_youtube_stream/`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Failed to create YouTube stream:', error);
      throw error;
    }
  }

  async getBatches() {
    try {
      return this.request('/batches/batches/');
    } catch (error) {
      console.error('Failed to get batches:', error);
      return [];
    }
  }

  // Dashboard Statistics - Real data for this teacher only
  async getDashboardStats() {
    try {
      // Get teacher's profile first
      const profile = await this.getProfile();
      if (!profile || !profile.id) {
        return {
          totalCourses: 0,
          totalStudents: 0,
          totalMaterials: 0,
          upcomingSessions: 0,
        };
      }
      
      // Try to get real data for this teacher only
      const courses = await this.getMyCourses();
      const batches = await this.getMyBatches();
      const materials = await this.request('/materials/materials/');
      
      // Handle different response formats for materials
      let materialsCount = 0;
      if (Array.isArray(materials)) {
        materialsCount = materials.length;
      } else if (materials && materials.results && Array.isArray(materials.results)) {
        materialsCount = materials.results.length;
      }
      
      // Calculate total students from teacher's batches
      let totalStudents = 0;
      if (Array.isArray(batches)) {
        totalStudents = batches.reduce((total, batch) => {
          if (batch.students && Array.isArray(batch.students)) {
            return total + batch.students.length;
          }
          return total + (batch.students_count || 0);
        }, 0);
      }
      
      return {
        totalCourses: Array.isArray(courses) ? courses.length : 0,
        totalStudents: totalStudents,
        totalMaterials: materialsCount,
        upcomingSessions: 0, // No real sessions data yet
      };
    } catch (error) {
      console.warn('Failed to fetch real dashboard stats, using fallback:', error);
      // Return zero values instead of mock data
      return {
        totalCourses: 0,
        totalStudents: 0,
        totalMaterials: 0,
        upcomingSessions: 0,
      };
    }
  }

  async getRecentActivity() {
    try {
      // Get teacher's profile first
      const profile = await this.getProfile();
      if (!profile || !profile.id) {
        return [];
      }
      
      // Try to get real activity data for this teacher only
      const courses = await this.getMyCourses();
      const materials = await this.request('/materials/materials/');
      
      const activities = [];
      
      // Add recent courses assigned to this teacher
      if (Array.isArray(courses) && courses.length > 0) {
        const recentCourses = courses.slice(0, 2);
        recentCourses.forEach(course => {
          activities.push({
            id: `course-${course.id}`,
            type: 'course',
            description: `Assigned to course: ${course.name || course.title || 'Untitled Course'}`,
            timestamp: course.created_at || new Date().toISOString(),
          });
        });
      }
      
      // Add recent materials created by this teacher
      let materialsArray = [];
      if (Array.isArray(materials)) {
        materialsArray = materials;
      } else if (materials && materials.results && Array.isArray(materials.results)) {
        materialsArray = materials.results;
      }
      
      if (materialsArray.length > 0) {
        const recentMaterials = materialsArray.slice(0, 2);
        recentMaterials.forEach(material => {
          activities.push({
            id: `material-${material.id}`,
            type: 'material',
            description: `Created material: ${material.title || 'Untitled Material'}`,
            timestamp: material.created_at || new Date().toISOString(),
          });
        });
      }
      
      // Sort by timestamp and return recent activities
      return activities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 5);
        
    } catch (error) {
      console.warn('Failed to fetch real activity data:', error);
      // Return empty array instead of mock data
      return [];
    }
  }
}

const apiService = new ApiService();
export default apiService; 